import React, { createContext, useState, useEffect, useContext } from 'react'
import { supabase } from '../utils/supabaseClient'
import { sendWelcomeEmail, addUserToList } from '../utils/emailUtils'
import envConfig from '../utils/envConfig'
import { listmonkClient } from '../utils/listmonkClient'

const AuthContext = createContext({
  user: null,
  loading: true,
  signUp: () => {},
  signIn: () => {},
  signInWithMagicLink: () => {},
  signOut: () => {},
  authError: null
})

// Global flag to prevent concurrent auth checks across component instances
let isAuthCheckInProgress = false;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [initialized, setInitialized] = useState(false)
  const [authError, setAuthError] = useState(null)

  // Add explicit token refresh check to ensure tokens are valid
  useEffect(() => {
    // Only run if we have a user and we're in a browser environment
    if (typeof window !== 'undefined' && user) {
      console.log('Setting up token refresh check for authenticated user');
      
      // Set up periodic session check every 5 minutes
      const tokenRefreshInterval = setInterval(async () => {
        try {
          // Check if the session is still valid
          const { data, error } = await supabase.auth.getSession();
          
          if (error) {
            console.error('Error refreshing session:', error);
            return;
          }
          
          // If no session exists but we think we're logged in, update state
          if (!data.session && user) {
            console.warn('Session expired during refresh check');
            setUser(null);
          }
        } catch (err) {
          console.error('Exception during token refresh check:', err);
        }
      }, 5 * 60 * 1000); // Check every 5 minutes
      
      // Clean up interval on unmount or user change
      return () => clearInterval(tokenRefreshInterval);
    }
  }, [user]); // Re-run when user changes

  // Function to check if user is subscribed
  const checkSubscriptionStatus = async (userId) => {
    try {
      // Check if they're subscribed in profiles table
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('is_subscribed')
        .eq('id', userId)
        .single();
      
      if (error) {
        console.error('Error checking subscription status:', error);
        return true; // If can't check, default to allowing login
      }
      
      // If profile exists and is_subscribed is explicitly false, block login
      if (profile && profile.is_subscribed === false) {
        console.log('User is unsubscribed, blocking login');
        return false;
      }
      
      // Allow login in all other cases
      return true;
    } catch (err) {
      console.error('Exception checking subscription:', err);
      return true; // Default to allowing login if error
    }
  };

  // Update the subscriber API URL based on environment
  const getSubscriberApiUrl = (path) => {
    const basePath = process.env.NODE_ENV === 'production' 
      ? envConfig.LISTMONK_URL  // Just use the base URL in production
      : '/api/listmonk-proxy';  // Proxy in development
    
    // Ensure path starts with /api
    const apiPath = path.startsWith('/api/') ? path : `/api${path}`;
    const fullPath = `${basePath}${apiPath}`;
    
    console.log(`🔗 ListMonk API URL: ${fullPath} (${process.env.NODE_ENV} environment)`);
    return fullPath;
  };

  // Add a debug function for ListMonk operations
  const logListMonkOperation = (operation, details) => {
    console.log(`📊 ListMonk Operation [${operation}]:`, {
      ...details,
      timestamp: new Date().toISOString(),
      environment: process.env.NODE_ENV
    });
  };

  // Helper function to check localStorage for auth token
  const checkLocalStorageAuth = () => {
    if (typeof window === 'undefined') return null;
    
    try {
      const storedToken = localStorage.getItem('sod-authToken');
      if (!storedToken) return null;
      
      const userData = JSON.parse(storedToken);
      if (!userData?.user) return null;
      
      console.log('Found auth token in localStorage, using it immediately');
      return userData.user;
    } catch (e) {
      console.error('Error parsing auth token from localStorage:', e);
      return null;
    }
  };

  // Safe auth check with coordination
  const safeAuthCheck = async (source = 'unknown') => {
    // Skip if another auth check is already in progress
    if (isAuthCheckInProgress) {
      console.log(`Auth check skipped (${source}): another check already in progress`);
      return null;
    }
    
    try {
      isAuthCheckInProgress = true;
      console.log(`Starting auth check (${source})`);
      
      const { data, error } = await supabase.auth.getSession();
      if (error) {
        console.error(`Auth check error (${source}):`, error);
        return null;
      }
      
      return data?.session?.user || null;
    } catch (err) {
      console.error(`Exception during auth check (${source}):`, err);
      return null;
    } finally {
      isAuthCheckInProgress = false;
    }
  };

  useEffect(() => {
    // Only run on the client side
    if (typeof window !== 'undefined') {
      // FIRST: Immediately check localStorage and use it for UI state
      const localStorageUser = checkLocalStorageAuth();
      if (localStorageUser) {
        setUser(localStorageUser);
        setLoading(false);
      }
      
      // Set a reasonable timeout to prevent infinite loading
      const loadingTimeout = setTimeout(() => {
        if (loading) {
          console.warn('Auth loading state timeout reached after 5s - forcing reset');
          setLoading(false);
          setInitialized(true);
        }
      }, 5000);
      
      // Get initial session - with a small random delay to prevent multiple tabs hammering the API
      const getInitialSession = async () => {
        // Small random delay (0-500ms) to prevent all tabs checking simultaneously
        await new Promise(resolve => setTimeout(resolve, Math.random() * 500));
        
        if (!isAuthCheckInProgress) {
          try {
            isAuthCheckInProgress = true;
            console.log('Starting initial auth session check');
            
            const { data, error } = await supabase.auth.getSession();
            if (error) {
              console.error('Error in initial session check:', error);
              // Don't change auth state on error if we already have a user from localStorage
              if (!user) setUser(null);
              setLoading(false);
              setInitialized(true);
              isAuthCheckInProgress = false;
              return;
            }
            
            const sessionUser = data?.session?.user || null;
            console.log('Auth session check completed, user found:', !!sessionUser);
            
            // If user exists in session, check subscription
            if (sessionUser) {
              try {
                const isSubscribed = await checkSubscriptionStatus(sessionUser.id);
                
                if (!isSubscribed) {
                  await supabase.auth.signOut();
                  setAuthError('Your subscription has ended. Please contact support to reactivate your account.');
                  setUser(null);
                } else {
                  setUser(sessionUser);
                }
              } catch (error) {
                console.error('Error checking subscription:', error);
                // Keep existing user on error
                setUser(sessionUser);
              }
            } else {
              // Only log out if we don't have a token in localStorage
              const localUser = checkLocalStorageAuth();
              if (!localUser) {
                setUser(null);
              } else {
                // Keep the localStorage user if session check failed
                console.log('Session not found but localStorage token exists, keeping user');
              }
            }
          } catch (err) {
            console.error('Exception in initial session:', err);
            // Don't log out on errors if we have localStorage user
            if (!user && !checkLocalStorageAuth()) {
              setUser(null);
            }
          } finally {
            setLoading(false);
            setInitialized(true);
            clearTimeout(loadingTimeout);
            isAuthCheckInProgress = false;
          }
        } else {
          console.log('Initial session check skipped - another check in progress');
          // Still need to reset loading state
          setLoading(false);
          setInitialized(true);
        }
      };
      
      getInitialSession();

      // Set up auth listener with improved error handling
      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        async (event, session) => {
          try {
            console.log(`Auth state change: ${event}`);
            const currentUser = session?.user || null;
            
            // Always update user on SIGNED_IN and SIGNED_OUT events
            if (event === 'SIGNED_IN') {
              try {
                const isSubscribed = await checkSubscriptionStatus(currentUser.id);
                if (!isSubscribed) {
                  await supabase.auth.signOut();
                  setAuthError('Your subscription has ended.');
                  setUser(null);
                  return;
                }
                setUser(currentUser);
                
                // Handle ListMonk in background
                setTimeout(async () => {
                  try {
                    const isEmailSignup = currentUser.app_metadata?.provider === 'email';
                    const isMagicLink = currentUser.app_metadata?.provider === 'magic-link';
                    
                    try {
                      // Check if user exists in ListMonk
                      const { exists } = await listmonkClient.getSubscriber(currentUser.email);
                      
                      if (!exists) {
                        // If they don't exist yet, add them with welcome email
                        console.log('🔍 [LISTMONK-AUTH] New user - adding to ListMonk List 3 with welcome');
                        await listmonkClient.addToList3AndSendWelcome(currentUser.email, {
                          name: currentUser.user_metadata?.full_name || '',
                          supabase_id: currentUser.id,
                          user_id: currentUser.id,
                          confirmation_status: 'confirmed',
                          created_at: currentUser.created_at || new Date().toISOString(),
                          auth_provider: currentUser.app_metadata?.provider || 'email',
                          user_role: 'free_user',
                          signup_date: new Date().toISOString(),
                          last_login: new Date().toISOString(),
                          source: isMagicLink ? 'magic_link_signup' : 'email_signup'
                        });
                      } else {
                        // User exists, just update their confirmation status
                        console.log('🔍 [LISTMONK-AUTH] User confirmed email - updating status in ListMonk');
                        await listmonkClient.updateConfirmationStatus(currentUser.email);
                      }
                      
                      // For magic link users, DON'T send additional welcome email
                      // This is now handled when they first request the magic link
                    } catch (error) {
                      if (!error.response || error.response.status !== 409) {
                        console.error('🔍 [LISTMONK-AUTH] Error:', error.message);
                      }
                    }
                  } catch (error) {
                    console.error('🔍 [LISTMONK-AUTH] Unexpected error:', error.message);
                  }
                }, 0);
              } catch (error) {
                console.error('Error in SIGNED_IN handler:', error);
                setUser(currentUser);
              }
            } else if (event === 'SIGNED_OUT') {
              setUser(null);
            }
          } catch (error) {
            console.error('Error in auth state change handler:', error);
          }
        }
      );

      // Simplified cross-tab synchronization with proper coordination
      const handleStorageEvent = (e) => {
        if (e.key === 'sod-authToken') {
          if (!e.newValue && user) {
            // Another tab logged out, sync logout here
            console.log('Auth token removed in another tab - logging out');
            setUser(null);
          } else if (e.newValue && !user) {
            // Another tab logged in, sync login here
            try {
              const userData = JSON.parse(e.newValue);
              if (userData?.user) {
                console.log('Auth token added in another tab - logging in');
                setUser(userData.user);
              }
            } catch (error) {
              console.error('Error parsing auth token from storage event:', error);
            }
          }
        }
      };
      
      // Debounced visibility check to avoid hammering the API
      let visibilityCheckTimeout = null;
      const handleVisibilityChange = () => {
        // Only check when tab becomes visible
        if (!document.hidden && initialized) {
          // Clear any existing timeout
          if (visibilityCheckTimeout) clearTimeout(visibilityCheckTimeout);
          
          // Set a new timeout with random delay
          visibilityCheckTimeout = setTimeout(async () => {
            // Only proceed if still visible and not checking auth already
            if (!document.hidden && !isAuthCheckInProgress) {
              // First check localStorage to avoid unnecessary API calls
              const localUser = checkLocalStorageAuth();
              
              // Only check with API if localStorage and current state differ
              if ((localUser && !user) || (!localUser && user)) {
                const sessionUser = await safeAuthCheck('visibility change');
                if (sessionUser) {
                  setUser(sessionUser);
                } else if (!sessionUser && !localUser) {
                  // Only log out if both API and localStorage say we're logged out
                  setUser(null);
                }
              }
            }
          }, 300 + Math.random() * 500); // Random delay between 300-800ms
        }
      };
      
      window.addEventListener('storage', handleStorageEvent);
      document.addEventListener('visibilitychange', handleVisibilityChange);
      
      return () => {
        window.removeEventListener('storage', handleStorageEvent);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        if (visibilityCheckTimeout) clearTimeout(visibilityCheckTimeout);
        subscription?.unsubscribe();
      };
    } else {
      // Set loading to false during SSR
      setLoading(false);
    }
  }, []);

  // Create a helper function outside the value object for retrying magic link sign-in
  const retryMagicLinkSignIn = async (email, redirectTo, retryCount = 0) => {
    if (typeof window !== 'undefined') {
      try {
        // Store the redirect URL for after authentication
        if (redirectTo) {
          try {
            localStorage.setItem('authRedirectUrl', redirectTo);
          } catch (e) {
            console.error('Error storing redirect URL:', e);
          }
        }
        
        // Register user to ListMonk immediately, regardless of whether they use the magic link
        try {
          console.log('Checking if magic link user exists in ListMonk:', email);
          
          // Check if user already exists in ListMonk
          const { exists } = await listmonkClient.getSubscriber(email);
          
          if (!exists) {
            // New user - register AND send welcome email in one operation
            console.log('New magic link user - registering and sending welcome email');
            await listmonkClient.addToList3AndSendWelcome(email, {
              source: 'magic_link_request',
              signup_date: new Date().toISOString(),
              registered_at: new Date().toISOString(),
              confirmation_status: 'unconfirmed'
            });
          } else {
            // Existing user - just update attributes, no welcome email
            console.log('Existing magic link user - updating attributes only, no welcome email');
            await listmonkClient.updateSubscriberByEmail(email, {
              confirmation_status: 'unconfirmed',
              last_magic_link_request: new Date().toISOString()
            });
          }
        } catch (error) {
          console.error('Error registering magic link user to ListMonk:', error);
          // Continue with magic link process even if ListMonk registration fails
        }
        
        // Call the Supabase magic link authentication
        const { data, error } = await supabase.auth.signInWithOtp({
          email,
          options: {
            emailRedirectTo: `${window.location.origin}/auth/confirm`,
          },
        });
        
        // If there's a network error and we haven't retried too many times, try again
        if (error && error.message && error.message.includes('network') && retryCount < 2) {
          console.log(`Network error during magic link sign-in, retrying (attempt ${retryCount + 1}/3)...`);
          // Wait a second before retrying
          await new Promise(resolve => setTimeout(resolve, 1000));
          return retryMagicLinkSignIn(email, redirectTo, retryCount + 1);
        }
        
        return { data, error };
      } catch (error) {
        console.error('Exception during magic link sign-in:', error);
        return { data: null, error };
      }
    }
    return Promise.resolve({ data: null, error: new Error('Cannot send magic link during SSR') });
  };

  // Improved sign out function that ensures proper cleanup
  const handleSignOut = async () => {
    if (typeof window !== 'undefined') {
      try {
        console.log('Signing out user...');
        
        // First, remove from localStorage to immediately prevent auto-login on refresh
        // This should also trigger storage events in other tabs
        try {
          localStorage.removeItem('sod-authToken');
          localStorage.removeItem('supabase.auth.token');
          localStorage.removeItem('authRedirectUrl');
          console.log('Auth tokens removed from localStorage');
        } catch (e) {
          console.error('Error removing auth tokens from localStorage:', e);
        }
        
        // Then update local state for immediate UI feedback
        setUser(null);
        
        // Clear any auth errors
        setAuthError(null);
        
        // Finally, perform Supabase signout (which also clears cookies)
        // This must come last to ensure UI is responsive even if this fails
        const { error } = await supabase.auth.signOut();
        if (error) {
          console.error('Error signing out from Supabase:', error);
        } else {
          console.log('Successfully signed out from Supabase');
        }
        
        // Force a page reload to ensure clean state
        // This is the most reliable way to ensure complete logout
        if (typeof window !== 'undefined') {
          window.location.href = '/';
        }
        
        return { error: null };
      } catch (err) {
        console.error('Exception during sign out:', err);
        return { error: err };
      }
    }
    return Promise.resolve({ error: null });
  };

  const value = {
    signUp: (data) => {
      if (typeof window !== 'undefined') {
        // Add redirectTo option to ensure auth confirmation works on localhost
        const promise = supabase.auth.signUp({
          ...data,
          options: {
            emailRedirectTo: `${window.location.origin}/auth/confirm`,
          }
        });
        
        // Register the user in ListMonk regardless of confirmation
        // This runs in the background and doesn't block the signup process
        setTimeout(async () => {
          try {
            const email = data.email;
            const name = data.options?.data?.full_name || '';
            
            console.log('Checking if user exists in ListMonk:', email);
            
            // Check if user already exists in ListMonk
            const { exists } = await listmonkClient.getSubscriber(email);
            
            if (!exists) {
              // New user - register AND send welcome email in one operation
              console.log('New user - registering and sending welcome email');
              await listmonkClient.addToList3AndSendWelcome(email, {
                name: name,
                source: 'email_signup',
                signup_date: new Date().toISOString(),
                registered_at: new Date().toISOString(),
                confirmation_status: 'unconfirmed'
              });
            } else {
              // Existing user - just update attributes, no welcome email
              console.log('Existing user - updating attributes only, no welcome email');
              await listmonkClient.updateSubscriberByEmail(email, {
                confirmation_status: 'unconfirmed'
              });
            }
          } catch (error) {
            console.error('Error registering user to ListMonk:', error);
          }
        }, 0);
        
        return promise;
      }
      return Promise.resolve({ user: null, error: new Error('Cannot sign up during SSR') })
    },
    signIn: (data) => typeof window !== 'undefined' ? supabase.auth.signInWithPassword(data) : Promise.resolve({ user: null, error: new Error('Cannot sign in during SSR') }),
    signInWithMagicLink: (email, redirectTo) => retryMagicLinkSignIn(email, redirectTo, 0),
    signOut: handleSignOut,
    user,
    loading,
    initialized,
    authError
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  return useContext(AuthContext)
}